import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Collapse,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControl, FormControlLabel, FormGroup, IconButton, InputLabel,
  LinearProgress, MenuItem, Select, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import TuneIcon from '@mui/icons-material/Tune';
import {
  downloadInvoiceExport,
  useCancelExportMutation,
  useCreateExportMutation,
  useExportJobsQuery,
  useExportOptionsQuery,
} from '../../services/invoiceExport';

const DEFAULT_COLUMNS = [
  'customer_code',
  'customer_name',
  'so_number',
  'invoice_no',
  'period_start_date',
  'period_end_date',
  'invoice_date',
  'due_date',
  'payment_status',
  'payment_type',
  'receipt_document',
  'payment_date',
  'invoice_grand_total',
  'service_names',
  'credit_note',
  'credit_note_amount',
  'contract_start_date',
  'contract_end_date',
  'invoice_amount_exclude_vat',
  'invoice_vat_amount',
  'delivery_job_status',
  'quotation',
  'credit_term',
  'po_number',
];

const statusColors = {
  Ready: 'success', Failed: 'error', Processing: 'warning', Queued: 'default',
  CancelRequested: 'warning', Cancelled: 'default',
};

const phaseLabels = {
  Preparing: 'กำลังเตรียมข้อมูล',
  Invoices: 'กำลังประมวลผลใบแจ้งหนี้',
  Interest: 'กำลังรวมรายการดอกเบี้ย',
  Finalizing: 'กำลังสร้างไฟล์',
};

function formatDate(value) {
  return value ? new Date(value).toLocaleString('th-TH') : '-';
}

function formatBytes(value) {
  if (!value) return '-';
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function formatDuration(value) {
  if (value === null || value === undefined) return '-';
  const seconds = Math.max(0, Math.floor(value));
  if (seconds < 60) return `${seconds} วินาที`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} นาที`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours} ชม. ${remainingMinutes} นาที` : `${hours} ชม.`;
}

export default function DataExport() {
  const [filters, setFilters] = useState({
    sorgcode: '', customer_code: '', start_date: '', end_date: '',
    search: '', payment_statuses: [],
  });
  const [includeInterest, setIncludeInterest] = useState(true);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [showColumns, setShowColumns] = useState(false);
  const [message, setMessage] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(5000);
  const [now, setNow] = useState(Date.now());
  const { data: optionsResponse } = useExportOptionsQuery();
  const {
    data: jobsResponse, isFetching, refetch,
  } = useExportJobsQuery(undefined, { pollingInterval, skipPollingIfUnfocused: true });
  const [createExport, { isLoading: isCreating }] = useCreateExportMutation();
  const [cancelExport, { isLoading: isCancelling }] = useCancelExportMutation();
  const options = optionsResponse?.data || {};
  const jobs = jobsResponse?.data || [];
  const allColumns = useMemo(
    () => [...(options.default_columns || []), ...(options.technical_columns || [])],
    [options.default_columns, options.technical_columns],
  );
  const activeStatuses = ['Queued', 'Processing', 'CancelRequested'];
  const hasActiveJob = jobs.some((job) => activeStatuses.includes(job.status));
  const activeJob = jobs.find((job) => activeStatuses.includes(job.status));

  useEffect(() => {
    setPollingInterval(hasActiveJob ? 5000 : 0);
    if (!hasActiveJob) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [hasActiveJob]);

  useEffect(() => {
    if (!hasActiveJob && message?.text === 'กำลังยกเลิกงาน Export') {
      setMessage(null);
    }
  }, [hasActiveJob, message?.text]);

  const setFilter = (key, value) => setFilters((current) => ({
    ...current, [key]: value,
  }));

  const toggleColumn = (column) => {
    setColumns((current) => current.includes(column)
      ? current.filter((item) => item !== column)
      : [...current, column]);
  };

  const handleCreate = async () => {
    setMessage(null);
    if (!columns.length) {
      setMessage({ severity: 'error', text: 'กรุณาเลือกอย่างน้อย 1 คอลัมน์' });
      return;
    }
    try {
      await createExport({
        filters: {
          ...filters,
          start_date: filters.start_date.replaceAll('-', ''),
          end_date: filters.end_date.replaceAll('-', ''),
        },
        columns,
        include_interest: includeInterest,
      }).unwrap();
      setMessage({ severity: 'success', text: 'สร้างงาน Export แล้ว' });
      refetch();
    } catch (error) {
      setMessage({
        severity: 'error',
        text: error?.data?.error?.message || 'ไม่สามารถสร้างงาน Export ได้',
      });
    }
  };

  const handleDownload = async (job) => {
    setDownloadingId(job.id);
    setMessage(null);
    try {
      await downloadInvoiceExport(job);
    } catch (error) {
      setMessage({ severity: 'error', text: error.message });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    try {
      await cancelExport(cancelTarget.id).unwrap();
      setMessage({ severity: 'success', text: 'กำลังยกเลิกงาน Export' });
      setCancelTarget(null);
      refetch();
    } catch (error) {
      setMessage({
        severity: 'error',
        text: error?.data?.error?.message || 'ไม่สามารถยกเลิกงาน Export ได้',
      });
    }
  };

  const activeElapsedSeconds = activeJob?.started_at
    ? Math.max(0, Math.floor((now - new Date(activeJob.started_at).getTime()) / 1000))
    : activeJob?.elapsed_seconds || 0;
  const hasProgressEstimate = Number.isFinite(activeJob?.progress_percent);
  const isNearComplete = activeJob?.estimated_total
    && activeJob.progress_count >= activeJob.estimated_total;

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto', pb: 5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Data Export</Typography>
          <Typography variant="body2" color="text.secondary">
            Invoice v2 พร้อม Payment Status, Credit Note, Partial และดอกเบี้ย
          </Typography>
        </Box>
        <Tooltip title="รีเฟรชรายการ">
          <span>
            <IconButton aria-label="รีเฟรชรายการ" onClick={refetch} disabled={isFetching}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {message && (
        <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2.5, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          <TextField label="SORG" size="small" value={filters.sorgcode}
            onChange={(event) => setFilter('sorgcode', event.target.value)} />
          <TextField label="Customer Code" size="small" value={filters.customer_code}
            onChange={(event) => setFilter('customer_code', event.target.value)} />
          <TextField label="Invoice / SO / Service" size="small" value={filters.search}
            onChange={(event) => setFilter('search', event.target.value)} />
          <TextField label="วันที่เริ่มต้น" type="date" size="small" value={filters.start_date}
            InputLabelProps={{ shrink: true }}
            onChange={(event) => setFilter('start_date', event.target.value)} />
          <TextField label="วันที่สิ้นสุด" type="date" size="small" value={filters.end_date}
            InputLabelProps={{ shrink: true }}
            onChange={(event) => setFilter('end_date', event.target.value)} />
          <FormControl size="small">
            <InputLabel>Payment Status</InputLabel>
            <Select multiple label="Payment Status" value={filters.payment_statuses}
              onChange={(event) => setFilter('payment_statuses', event.target.value)}
              renderValue={(selected) => selected.join(', ')}>
              {(options.payment_statuses || []).map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox checked={filters.payment_statuses.includes(status)} />{status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mt: 2, gap: 2 }}>
          <FormControlLabel control={<Switch checked={includeInterest}
            onChange={(event) => setIncludeInterest(event.target.checked)} />}
            label="รวมรายการดอกเบี้ย" />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button startIcon={<TuneIcon />} onClick={() => setShowColumns((value) => !value)}>
              คอลัมน์ ({columns.length})
            </Button>
            <Button variant="contained" startIcon={isCreating ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
              disabled={isCreating || hasActiveJob} onClick={handleCreate}>
              สร้างไฟล์ CSV
            </Button>
          </Box>
        </Box>

        <Collapse in={showColumns}>
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 2, pt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">เลือกคอลัมน์</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" disabled={!allColumns.length || allColumns.every((column) => columns.includes(column))}
                  onClick={() => setColumns([...allColumns])}>
                  เลือกทั้งหมด (Select all)
                </Button>
                <Button size="small" onClick={() => setColumns(DEFAULT_COLUMNS)}>คืนค่าชุดหลัก</Button>
              </Box>
            </Box>
            <FormGroup sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' } }}>
              {allColumns.map((column) => (
                <FormControlLabel key={column}
                  control={<Checkbox size="small" checked={columns.includes(column)} onChange={() => toggleColumn(column)} />}
                  label={<Typography variant="body2">{column}</Typography>} />
              ))}
            </FormGroup>
          </Box>
        </Collapse>
      </Box>

      {activeJob && (
        <Box sx={{ mt: 3, px: 2.5, py: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {activeJob.status === 'Queued' && 'รอเริ่มประมวลผล'}
                {activeJob.status === 'Processing' && (phaseLabels[activeJob.progress_phase] || 'กำลัง Export')}
                {activeJob.status === 'CancelRequested' && 'กำลังยกเลิกงาน Export'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                สามารถไปหน้าอื่นหรือปิดหน้านี้ได้ งานจะทำต่อที่ระบบ
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip size="small" label={activeJob.status} color={statusColors[activeJob.status]} />
              {['Queued', 'Processing'].includes(activeJob.status) && (
                <Button color="error" size="small" variant="outlined"
                  startIcon={<CancelOutlinedIcon />} onClick={() => setCancelTarget(activeJob)}>
                  ยกเลิก
                </Button>
              )}
              {activeJob.status === 'CancelRequested' && <CircularProgress size={20} />}
            </Box>
          </Box>

          <LinearProgress
            variant={hasProgressEstimate ? 'determinate' : 'indeterminate'}
            value={hasProgressEstimate ? activeJob.progress_percent : undefined}
            sx={{ height: 8, borderRadius: 1 }}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mt: 1.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Records</Typography>
              <Typography variant="body2" fontWeight={600}>
                {(activeJob.progress_count || 0).toLocaleString()}
                {activeJob.estimated_total ? ` / ประมาณ ${activeJob.estimated_total.toLocaleString()}` : ''}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">ความคืบหน้า</Typography>
              <Typography variant="body2" fontWeight={600}>
                {hasProgressEstimate
                  ? (isNearComplete ? 'ใกล้เสร็จแล้ว' : `ประมาณ ${activeJob.progress_percent}%`)
                  : 'กำลังประเมิน'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">ใช้เวลาแล้ว</Typography>
              <Typography variant="body2" fontWeight={600}>{formatDuration(activeElapsedSeconds)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">เวลาที่เหลือ</Typography>
              <Typography variant="body2" fontWeight={600}>
                {isNearComplete ? 'ใกล้เสร็จแล้ว' : formatDuration(activeJob.estimated_remaining_seconds)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Typography variant="h6" sx={{ mt: 4, mb: 1.5 }}>ประวัติการ Export</Typography>
      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>สร้างเมื่อ</TableCell><TableCell>สถานะ</TableCell>
            <TableCell align="right">Records</TableCell><TableCell>ไฟล์</TableCell>
            <TableCell>หมดอายุ</TableCell><TableCell align="center">ดาวน์โหลด</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {jobs.length === 0 && <TableRow><TableCell colSpan={6} align="center">ยังไม่มีงาน Export</TableCell></TableRow>}
            {jobs.map((job) => (
              <TableRow key={job.id} hover>
                <TableCell>{formatDate(job.created_at)}</TableCell>
                <TableCell>
                  <Chip size="small" label={job.status} color={statusColors[job.status]} />
                  {['Processing', 'CancelRequested'].includes(job.status) && (
                    <Typography display="block" variant="caption" color="text.secondary">
                      {job.status === 'CancelRequested'
                        ? 'กำลังยกเลิก' : phaseLabels[job.progress_phase] || 'กำลัง Export'}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {(job.row_count || job.progress_count || 0).toLocaleString()}
                  {job.status === 'Processing' && job.estimated_total
                    ? ` / ~${job.estimated_total.toLocaleString()}` : ''}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{job.filename || '-'}</Typography>
                  <Typography variant="caption" color={job.status === 'Failed' ? 'error' : 'text.secondary'}>
                    {job.error_message || formatBytes(job.file_size)}
                  </Typography>
                </TableCell>
                <TableCell>{formatDate(job.expires_at)}</TableCell>
                <TableCell align="center">
                  <Tooltip title={downloadingId === job.id ? 'กำลังดาวน์โหลด' : 'ดาวน์โหลด CSV'}>
                    <span><IconButton aria-label="ดาวน์โหลด CSV" size="small"
                      disabled={job.status !== 'Ready' || downloadingId === job.id}
                      onClick={() => handleDownload(job)}>
                      {downloadingId === job.id
                        ? <CircularProgress size={20} /> : <CloudDownloadOutlinedIcon />}
                    </IconButton></span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(cancelTarget)} onClose={() => !isCancelling && setCancelTarget(null)}>
        <DialogTitle>ยกเลิกงาน Export?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ไฟล์ที่กำลังสร้างจะถูกลบ และสามารถแก้ตัวกรองแล้วสร้างงานใหม่ได้หลังยกเลิกเสร็จ
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)} disabled={isCancelling}>กลับ</Button>
          <Button color="error" variant="contained" onClick={handleCancel}
            disabled={isCancelling} startIcon={isCancelling ? <CircularProgress size={18} color="inherit" /> : <CancelOutlinedIcon />}>
            ยืนยันยกเลิก
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
